<script>

    import { onMount } from 'svelte'
    import { toast } from 'bulma-toast'

    import View from '../$layouts/view.layout.svelte'
    import PageHeader from '../$components/page.header.svelte'
    import Button from '../$components/button.svelte'
    import ZonesService from '../$services/zones.service'
    import ZoneMap from './component.map.svelte'
    import ZoneForm from './component.form.svelte'
    import ZoneList from './component.list.svelte'

    const MIN_VERTICES = 3
    const COLORS = ['#098e37', '#2563eb', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#db2777']

    let mapRef
    let mapReady = false
    let zones = []
    let loading = false
    let saving = false
    let deleting = false

    // idle: sin zona abierta | drawing: agregando vértices | editing: polígono cerrado
    let mode = 'idle'
    let form = null
    let formKey = 0
    let path = []
    let pointCount = 0
    let snapshot = ''

    $: currentColor = form ? form.color : COLORS[0]
    $: canSave = mode === 'editing' && path.length >= MIN_VERTICES

    onMount(load)

    async function load() {
        loading = true
        const resp = await ZonesService.getZones()
        loading = false

        if (!resp.success)
            return notify(resp.error.message, 'is-danger')

        zones = resp.data
    }

    function createZone() {
        if (!mapReady || !confirmDiscard()) return

        form = {
            _id: null,
            name: '',
            code: '',
            driverId: null,
            driverEmail: null,
            color: COLORS[zones.length % COLORS.length],
        }
        formKey += 1
        path = []
        pointCount = 0
        mode = 'drawing'
        mapRef.startDrawing()
        snapshot = currentState()
    }

    function selectZone(zone) {
        if (!mapReady || (form && String(form._id) === String(zone._id)) || !confirmDiscard()) return

        form = {
            _id: zone._id,
            name: zone.name,
            code: zone.code,
            driverId: zone.driverId,
            driverEmail: zone.driver ? zone.driver.email : null,
            color: zone.color,
        }
        formKey += 1
        mode = 'editing'
        mapRef.editPath(zone.path)
        snapshot = currentState()
    }

    function close() {
        mapRef.clearDraft()
        form = null
        path = []
        pointCount = 0
        mode = 'idle'
        snapshot = ''
    }

    function cancel() {
        if (confirmDiscard()) close()
    }

    function onKeydown(event) {
        if (event.key === 'Escape' && mode === 'drawing') close()
    }

    function onPath(event) {
        path = event.detail.path

        if (mode === 'drawing') {
            mode = 'editing'
            snapshot = ''
        }
    }

    function currentState() {
        const { driverEmail, ...fields } = form
        return JSON.stringify({ ...fields, path })
    }

    function confirmDiscard() {
        if (!form || currentState() === snapshot) return true

        return window.confirm('Hay cambios sin guardar en la zona. ¿Descartarlos?')
    }

    async function save() {
        if (!canSave) return

        if (!form.driverId)
            return notify('Selecciona un driver de la lista', 'is-danger')

        const data = {
            name: form.name,
            code: form.code,
            color: form.color,
            driverId: form.driverId,
            path,
        }

        saving = true
        const resp = form._id
            ? await ZonesService.updateZone(form._id, data)
            : await ZonesService.createZone(data)
        saving = false

        if (!resp.success)
            return notify(resp.error.message, 'is-danger')

        notify('Zona guardada', 'is-success')

        if (resp.data.overlaps && resp.data.overlaps.length) {
            const names = resp.data.overlaps.map(zone => zone.name).join(', ')
            notify(`La zona se encima con: ${names}`, 'is-warning', 6000)
        }

        close()
        await load()
    }

    async function remove() {
        deleting = true
        const resp = await ZonesService.deleteZone(form._id)
        deleting = false

        if (!resp.success)
            return notify(resp.error.message, 'is-danger')

        notify('Zona borrada', 'is-success')
        close()
        await load()
    }

    function notify(message, type, duration = 3000) {
        toast({ message, type, duration })
    }

</script>

<style>
    .zones {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 320px;
        gap: 16px;
        align-items: start;
    }

    .zones__panel {
        padding: 16px;
        border-radius: 20px;
        background: #fff;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    }

    .zones__new {
        margin-bottom: 12px;
    }

    .zones__toolbar {
        position: absolute;
        z-index: 2;
        top: 12px;
        left: 12px;
        right: 60px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 16px;
        background: #fff;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    }

    .zones__hint {
        flex: 1 1 200px;
        margin: 0;
        font-size: 13px;
        color: #3a3a3a;
    }

    @media (max-width: 900px) {
        .zones {
            grid-template-columns: minmax(0, 1fr);
        }
    }
</style>

<svelte:window on:keydown={onKeydown} />

<View>
    <PageHeader title="Zonas" subtitle="Zonas de entrega TIL y su driver asignado" />

    <div class="zones">
        <ZoneMap
            bind:this={mapRef}
            {zones}
            hiddenId={form && form._id}
            interactive={mode === 'idle'}
            color={currentColor}
            on:ready={() => mapReady = true}
            on:select={event => selectZone(event.detail)}
            on:drawing={event => pointCount = event.detail.count}
            on:path={onPath}
        >
            {#if mode === 'drawing'}
                <div class="zones__toolbar">
                    <p class="zones__hint">
                        Haz clic para agregar puntos ({pointCount}). Cierra en el primer punto o con Terminar.
                    </p>
                    <Button text="Deshacer punto" size="small" on:click={() => mapRef.undoPoint()} />
                    <Button
                        text="Terminar"
                        color="primary"
                        size="small"
                        on:click={() => pointCount >= MIN_VERTICES
                            ? mapRef.finishDrawing()
                            : notify('La zona necesita al menos 3 puntos', 'is-danger')}
                    />
                </div>
            {/if}
        </ZoneMap>

        <aside class="zones__panel">
            {#if form}
                {#key formKey}
                    <ZoneForm
                        bind:zone={form}
                        {canSave}
                        {saving}
                        {deleting}
                        on:save={save}
                        on:cancel={cancel}
                        on:delete={remove}
                    />
                {/key}
            {:else}
                <div class="zones__new">
                    <Button
                        text="Nueva zona"
                        icon="draw-polygon"
                        color="primary"
                        fullwidth
                        loading={!mapReady}
                        on:click={createZone}
                    />
                </div>
                <ZoneList
                    {zones}
                    {loading}
                    selectedId={null}
                    on:select={event => selectZone(event.detail)}
                />
            {/if}
        </aside>
    </div>
</View>
